import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import searchIcon from "@assets/icons/search-icon.svg";
import styles from "./Dashboard.module.css";
import Header from "@components/header/Header";
import EvaluationGroupActionModal from "@modals/evaluationgroupaction/EvaluationGroupActionModal";
import UnAssignConfirmation from "@modals/unassignconfirmation/UnAssignConfirmation";
import Pagination from "@components/common/pagination/Pagination";
import { useDispatch, useSelector } from "react-redux";
import { setComponentToShow } from "@store/ModalToShow";
import arrRightIcon from "@assets/icons/arr_Right.svg";
import type { EvaluationGroup } from "@services/evaluationService";
import {
  useAssignedEvaluationTaskV3,
  useEvaluationGroups,
  useEvaluationMutations,
} from "@hooks/evaluation";

function Dashboard() {
  const {
    task: assignedEvaluationTask,
    isLoading: isAssignedTaskLoading,
    error: assignedTaskError,
    hasTask,
    refetch: refetchAssignedTask,
  } = useAssignedEvaluationTaskV3();
  const {
    groups: evaluationGroups,
    isLoading: isEvaluationGroupsLoading,
    error: evaluationGroupsError,
    hasGroups,
    refetch: refetchEvaluationGroups,
  } = useEvaluationGroups();
  const { unassignTask, isUnassigning } = useEvaluationMutations();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const dispatch = useDispatch();
  const componentName = useSelector(
    (state: any) => state.modalToShow.componentName
  );
  const navigate = useNavigate();

  const filteredData = useMemo(() => {
    let filtered = evaluationGroups ? [...evaluationGroups] : [];
    if (searchTerm) {
      filtered = filtered.filter((group) =>
        group.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  }, [searchTerm, evaluationGroups]);

  const totalPages = Math.ceil(filteredData?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleSelectTask = (groupId: string) => {
    if (hasTask) return; // Prevent selection when task is assigned
    setSelectedGroupId(groupId);
    dispatch(setComponentToShow("EvaluationGroupAction"));
  };

  const handleClearTaskSelection = () => {
    setSelectedGroupId(null);
    dispatch(setComponentToShow(""));
  };

  const selectedGroupName = useMemo(() => {
    if (!selectedGroupId) return "";
    return (
      evaluationGroups.find((group: EvaluationGroup) => group.id === selectedGroupId)?.name ??
      ""
    );
  }, [selectedGroupId, evaluationGroups]);

  const handleProceed = (evaluationPhaseId: string) => {
    if (!selectedGroupId) return;

    navigate(
      `/evaluation?evaluation_group_id=${selectedGroupId}&evaluation_phase_id=${evaluationPhaseId}`,
      {
        state: {
          mode: "assign",
          evaluationGroupId: selectedGroupId,
          evaluationPhaseId,
        },
      }
    );
    handleClearTaskSelection();
  };

  const handleResumeEvaluation = () => {
    if (!assignedEvaluationTask?.id) return;
    // Navigate with assignmentId in router state for resume flow
    navigate(`/evaluation?evaluation_group_id=${assignedEvaluationTask.evaluation_group_id}&evaluation_phase_id=${assignedEvaluationTask.evaluation_phase_id}`, {
      state: { mode: "resume", assignmentId: assignedEvaluationTask.assignmentId },
    });
  };

  const handleUnAssign = async () => {
    if (!assignedEvaluationTask?.assignmentId) return;
    try {
      await unassignTask(assignedEvaluationTask.assignmentId);
      // Refetch both queries after successful unassign
      await refetchAssignedTask();
      await refetchEvaluationGroups();
      dispatch(setComponentToShow(""));
    } catch (error) {
      // Error handling is done in the mutation hook via toast
      console.error("Failed to unassign task:", error);
    }
  };

  const renderModal = () => {
    switch (componentName) {
      case "EvaluationGroupAction":
        return (
          <EvaluationGroupActionModal
            isOpen={true}
            groupName={selectedGroupName}
            evaluationGroupId={selectedGroupId ?? ""}
            onClose={() => handleClearTaskSelection()}
            onProceed={handleProceed}
          />
        );
      case "UnAssignConfirmation":
        return (
          <UnAssignConfirmation
            isOpen={true}
            onClose={() => handleClearTaskSelection()}
            onUnAssign={handleUnAssign}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Header />
      <div className={styles.dashboardContainer}>
        <div className={styles.mainContent}>
          <div className={styles.header}>
            <div className={styles.title}>
              <span>Evaluation Groups</span>
            </div>
          </div>

          <div className={styles.searchContainer}>
            <div className={styles.inputContainer}>
              <input
                type="text"
                placeholder="Search by Evaluation Group Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <img src={searchIcon} alt="Search Icon" />
            </div>
            <div className={styles.searchButtonContainer}>
              <motion.button
                className={styles.searchButton}
                onClick={handleSearch}
                whileTap={{ translateY: 2 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                Search
              </motion.button>
            </div>
          </div>
          <div className={styles.assignedEvaluationTaskContainer}>
            {isAssignedTaskLoading && (
              <div className={styles.evaluationGroupsListTable}>
                <div className={styles.loadingMessage}>
                  Loading assigned task...
                </div>
              </div>
            )}
            {assignedTaskError && (
              <div className={styles.evaluationGroupsListTable}>
                <div className={styles.errorMessage}>
                  Error loading assigned task. Please try again.
                </div>
              </div>
            )}
            {hasTask && assignedEvaluationTask && (
              <div className={styles.evaluationGroupsListTable}>
                <table>
                  <thead>
                    <tr>
                      <th>Assigned Evaluation Task</th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr key={assignedEvaluationTask.id}>
                      <td>{assignedEvaluationTask.id}</td>
                      <td>
                        <button
                          className={styles.unassignButton}
                          onClick={() =>
                            dispatch(setComponentToShow("UnAssignConfirmation"))
                          }
                          disabled={isUnassigning}
                        >
                          {isUnassigning ? "Unassigning..." : "unassign"}
                        </button>
                      </td>
                      <td>
                        <button
                          className={styles.evaluateButton}
                          onClick={handleResumeEvaluation}
                        >
                          resume evaluation
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className={styles.evaluationGroupsListContainer}>
            {hasTask && (
              <div className={styles.disabledMessage}>
                Finish or unassign your current task to pick a new one.
              </div>
            )}
            <div className={styles.evaluationGroupsListActionsContainer}>
              {!isEvaluationGroupsLoading && !evaluationGroupsError && (
                <span className={styles.groupCount}>
                  {evaluationGroups.length}{" "}
                  {evaluationGroups.length === 1 ? "group" : "groups"}
                </span>
              )}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                disabled={hasTask}
              />
            </div>

            <div className={styles.evaluationGroupsListTable}>
              {isEvaluationGroupsLoading && (
                <div className={styles.loadingMessage}>
                  Loading evaluation groups...
                </div>
              )}
              {evaluationGroupsError && (
                <div className={styles.errorMessage}>
                  Error loading evaluation groups.{" "}
                  <button
                    onClick={() => refetchEvaluationGroups()}
                    className={styles.retryButton}
                  >
                    Retry
                  </button>
                </div>
              )}
              {!isEvaluationGroupsLoading &&
                !evaluationGroupsError &&
                (currentData.length > 0 ? (
                  <table
                    className={`${hasTask ? styles.disabledTable : ""}`}
                    aria-disabled={hasTask}
                  >
                    <thead>
                      <tr>
                        <th>Evaluation Group</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentData.map((group) => (
                        <tr key={group.id}>
                          <td>{group.name}</td>
                          <td>
                            <button
                              type="button"
                              className={styles.groupNavigateButton}
                              onClick={() => handleSelectTask(group.id)}
                              disabled={hasTask}
                              aria-disabled={hasTask}
                              aria-label={`Open ${group.name}`}
                            >
                              <img src={arrRightIcon} alt="" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className={styles.emptyMessage}>
                    {hasGroups
                      ? "No evaluation groups match your search criteria."
                      : "No evaluation groups found."}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
      {renderModal()}
    </>
  );
}

export default Dashboard;
